import { GHL_NAME_FORMAT_MAX } from '../constants/gohighlevel.js'
import type { CalendarDay } from '../validators/temporal.validator.js'

/**
 * How a company names its events, as a format an event name is built from when
 * one is created from a GoHighLevel lead (docs/gohighlevel.spec.md, "The event
 * name").
 *
 * Companies name events their own way — `Jane Doe & John Smith` at one,
 * `6.12.27 Doe | Smith` at another — so a format is literal text with parts in
 * braces:
 *
 * - `{first}` `{last}` — the lead's own first and last name
 * - `{partnerFirst}` `{partnerLast}` — their partner's
 * - `{date:<pattern>}` — the event date, written with `M MM MMM MMMM D DD YY YYYY`
 *   and separators, so a format says for itself whether the day is zero-padded
 *
 * A part with nothing to fill it is left out, and whatever joined it (`&`, `+`,
 * `|`, `and`) goes with it, rather than leaving `Jane Doe &` behind.
 */

/** What a lead knows about the couple. Any of it may be missing. */
export interface EventNameParts {
  first: string | null
  last: string | null
  partnerFirst: string | null
  partnerLast: string | null
}

export const NAME_TOKENS = ['first', 'last', 'partnerFirst', 'partnerLast'] as const
type NameToken = (typeof NAME_TOKENS)[number]

/** What `{date:…}` may contain: the date letters and plain separators. */
export const DATE_TOKEN_PATTERN = /^(?:MMMM|MMM|MM|M|DD|D|YYYY|YY|[.\-/ ,])+$/

export const DEFAULT_NAME_FORMAT = '{first} {last} & {partnerFirst} {partnerLast}'

/** The longest event name there can be (the event's own limit). */
const EVENT_NAME_MAX = 160

const DATE_PRESET = '{date:M.D.YY} {last} | {partnerLast}'
const PADDED_DATE_PRESET = '{date:MM.DD.YY} {last} | {partnerLast}'

/**
 * The formats venues were found to use, each with the shape of a name written in
 * it — the order is the order `suggestNameFormat` prefers them in, most specific
 * first.
 */
export const NAME_FORMAT_PRESETS: readonly { format: string; match: RegExp }[] = [
  { format: DATE_PRESET, match: /^\d{1,2}\.\d{1,2}\.\d{2} \S.* \| \S.*$/u },
  { format: "{first} + {partnerFirst}'s Wedding", match: /^\S+ \+ \S+['’]s Wedding$/u },
  { format: 'Wedding {first} {last} & {partnerFirst} {partnerLast}', match: /^Wedding \S.* & \S.*$/u },
  { format: DEFAULT_NAME_FORMAT, match: /^\p{L}\S*(?: \S+)+ & \p{L}\S*(?: \S+)+$/u },
  { format: '{first} {last} and {partnerFirst} {partnerLast}', match: /^\p{L}\S*(?: \S+)+ and \p{L}\S*(?: \S+)+$/u },
  { format: '{first} & {partnerFirst}', match: /^\S+ & \S+$/u },
]

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** The date letters, longest first so `MMMM` is never read as four `M`s. */
const DATE_LETTERS = ['YYYY', 'MMMM', 'MMM', 'MM', 'DD', 'YY', 'M', 'D'] as const

function isNameToken(value: string): value is NameToken {
  return NAME_TOKENS.some((t) => t === value)
}

/** `date` is a calendar day, `YYYY-MM-DD`. The brand is what makes the slices a date. */
function formatDate(date: CalendarDay, pattern: string): string {
  const year = date.slice(0, 4)
  const month = Number(date.slice(5, 7))
  const day = Number(date.slice(8, 10))
  const values: Record<(typeof DATE_LETTERS)[number], string> = {
    YYYY: year,
    YY: year.slice(2),
    MMMM: MONTHS[month - 1] ?? '',
    MMM: (MONTHS[month - 1] ?? '').slice(0, 3),
    MM: String(month).padStart(2, '0'),
    M: String(month),
    DD: String(day).padStart(2, '0'),
    D: String(day),
  }
  let out = ''
  let i = 0
  while (i < pattern.length) {
    const letter = DATE_LETTERS.find((l) => pattern.startsWith(l, i))
    if (letter === undefined) {
      out += pattern[i]
      i += 1
    } else {
      out += values[letter]
      i += letter.length
    }
  }
  return out
}

/** Why a format can't be used, in words for the person typing it; null when it can. */
export function validateNameFormat(format: string): string | null {
  if (format.length > GHL_NAME_FORMAT_MAX) return `Use at most ${String(GHL_NAME_FORMAT_MAX)} characters.`
  let hasName = false
  for (const match of format.matchAll(/\{([^{}]*)\}/g)) {
    const inner = match[1] ?? ''
    if (isNameToken(inner)) {
      hasName = true
      continue
    }
    const date = /^date:(.+)$/.exec(inner)
    if (date?.[1] !== undefined && DATE_TOKEN_PATTERN.test(date[1])) continue
    return `Unknown part {${inner}}.`
  }
  if (/[{}]/.test(format.replace(/\{[^{}]*\}/g, ''))) return 'Close every { with a }.'
  if (!hasName) return 'Include at least one name.'
  return null
}

/** Joiners between two parts of a name. */
const JOINER = '(?:&|\\+|\\||\\band\\b)'

/**
 * Leave out what's missing and the joiner that went with it: `Jane Doe &` becomes
 * `Jane Doe`, `Jane + 's Wedding` becomes `Jane's Wedding`.
 */
function tidy(name: string): string {
  let s = name
  let previous: string
  do {
    previous = s
    s = s.replace(/\s+/g, ' ').trim()
    s = s.replace(new RegExp(`\\s*${JOINER}\\s*(['’]s)\\b`, 'giu'), '$1')
    s = s.replace(/^(?:&|\+|\||and\b)\s*/iu, '')
    s = s.replace(/^['’]s\b\s*/u, '')
    s = s.replace(/\s*(?:&|\+|\|)$/u, '').replace(/\s+and$/iu, '')
    s = s.replace(/(&|\+|\|)\s*(?:&|\+|\|)/gu, '$1')
  } while (s !== previous)
  return s
}

/**
 * The event's name, from a format, what the lead knows about the couple, and the
 * event's date when it has one. May be empty, when nothing at all
 * was known; the form then asks for a name as it always does.
 */
export function renderEventName(format: string, parts: EventNameParts, date: CalendarDay | null): string {
  const filled = format.replace(/\{([^{}]*)\}/g, (_whole, inner: string) => {
    if (isNameToken(inner)) return (parts[inner] ?? '').trim()
    const pattern = /^date:(.+)$/.exec(inner)?.[1]
    if (pattern !== undefined && DATE_TOKEN_PATTERN.test(pattern)) return date === null ? '' : formatDate(date, pattern)
    return ''
  })
  return tidy(filled).slice(0, EVENT_NAME_MAX).trim()
}

/**
 * The format most of the existing events already follow, or the default
 * when there is no clear pattern: at least three names, and at least half of
 * them, must agree. For dated names, whether the day and month are zero-padded is
 * copied from the names too.
 */
export function suggestNameFormat(names: readonly string[]): string {
  const counts = new Map<string, number>()
  const dated: string[] = []
  for (const raw of names) {
    const name = raw.trim()
    const preset = NAME_FORMAT_PRESETS.find((p) => p.match.test(name))
    if (preset === undefined) continue
    counts.set(preset.format, (counts.get(preset.format) ?? 0) + 1)
    if (preset.format === DATE_PRESET) dated.push(name)
  }
  let best: { format: string; count: number } | null = null
  for (const preset of NAME_FORMAT_PRESETS) {
    const count = counts.get(preset.format) ?? 0
    if (count > 0 && (best === null || count > best.count)) best = { format: preset.format, count }
  }
  if (best === null || best.count < 3 || best.count / names.length < 0.5) return DEFAULT_NAME_FORMAT
  if (best.format === DATE_PRESET) {
    const padded = dated.filter((n) => /^\d{2}\.\d{2}\./.test(n)).length
    return padded > dated.length / 2 ? PADDED_DATE_PRESET : DATE_PRESET
  }
  return best.format
}

/**
 * One "partner's name" field into first and last: the first word, then the rest.
 * Blank parts are null, so a format leaves them out rather than guessing.
 */
export function splitPersonName(value: string | null | undefined): { first: string | null; last: string | null } {
  const words = (value ?? '').trim().split(/\s+/).filter((w) => w !== '')
  const [first, ...rest] = words
  return { first: first ?? null, last: rest.length > 0 ? rest.join(' ') : null }
}
